/**
 * FilesResultsPage — список всех датасетов проекта.
 *
 * URL: /files/results?project={project_id}
 * Если project_id не указан — берём из store (после useProjectBootstrap).
 *
 * Двухзонная компоновка (Block 5 / C-блок +4, фидбэк Janni):
 *  - Вкладка «Готово к работе» — только `ready` (прошёл трансформацию).
 *    Действие «Обработать →» ведёт в /process/search или /process/generation
 *    с уже выбранным dataset в URL.
 *  - Вкладка «Загружено — нужна трансформация» — uploaded/parsing/failed.
 *    Действие «Трансформировать →» ведёт в /files/transform.
 *
 * «Открыть» и «Скачать» — всегда. «Удалить» — с подтверждением.
 * «Перепарсить» — только для failed.
 *
 * P1.3 (2026-08-14): display-таблица мигрирована на единый `<DataTable>`.
 * Каркас (pagination/pin/sort/filter/auto-scroll) делегирован; здесь только
 * column-def'ы + cell-рендеры + aux-banner-строки (download-error /
 * parsing-timeout, testids сохранены) через `renderAuxRow`. Transform-движок,
 * reparse, apply_search — НЕ трогаем.
 *
 * Логика picker'ов в /process/* уже фильтрует d.status === 'ready',
 * поэтому кнопка «Обработать» физически ведёт только на готовый dataset.
 *
 * Резолв currentProjectId (stale id → «Default» → lazy-create) выполняет
 * useProjectBootstrap в Shell. На этой странице — только sync URL ↔ store.
 *
 * Phase 1.7 (Files UX Refactor 2026-08-06): full-reload /files/results без
 * bootstrap-guard вызывал 404-spam при stale ?project=… URL. Фикс — гард
 * `bootstrapReady` (useProjects.isFetched + валидный storeProjectId), аналог
 * FilesUploadPage. Smooth UX: Loader2 пока bootstrap поднимает store.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import {
  CheckCircle2,
  Circle,
  Eye,
  Loader2,
  Pencil,
  Play,
  RotateCw,
  Search as SearchIcon,
  Trash2,
  Upload,
} from 'lucide-react';

import {
  type Dataset,
  type DatasetStatus,
  useDeleteDataset,
  useProjectDatasets,
  useReparseDataset,
} from '@/hooks/useFiles';
import { useDownloadDataset } from '@/hooks/useDownloadDataset';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { FileTypeIcon } from '@/components/common/FileTypeIcon';
import { DataPreviewModal } from '@/components/files/DataPreviewModal';
import { DownloadButton } from '@/components/files/DownloadButton';
import { DataTable } from '@/components/shared/DataTable';
import { useActiveDataset } from '@/hooks/useActiveDataset';
import { useParsingTimeout } from '@/hooks/useParsingTimeout';
import { useProjectStore } from '@/store/projectStore';
import { useProjects, useSetActiveDataset } from '@/hooks/useProjects';
import { cn } from '@/lib/utils';

type TabKey = 'ready' | 'pending';

const STATUS_LABEL_KEY: Record<DatasetStatus, string> = {
  uploaded: 'files.status.uploaded',
  parsing: 'files.status.parsing',
  ready: 'files.status.ready',
  failed: 'files.status.failed',
};

const STATUS_TONE: Record<DatasetStatus, string> = {
  uploaded: 'bg-surface-700/40 text-surface-300 border-surface-700',
  parsing: 'bg-info-tint/15 text-info300 border-info-tint/30',
  ready: 'bg-success-tint/15 text-success border-success-tint/30',
  failed: 'bg-danger-tint/15 text-danger border-danger-tint/30',
};

// Для подсчёта «нужна трансформация» — всё, что не ready.
// Phase 1.4 (Files UX Refactor 2026-08-06): 'transforming' удалён — dead state.
const PENDING_STATUSES: ReadonlySet<DatasetStatus> = new Set<DatasetStatus>([
  'uploaded',
  'parsing',
  'failed',
]);

// Число колонок в <DataTable> — нужно для colSpan aux-banner-строк.
const COL_COUNT = 7;

export function FilesResultsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project');
  const storeProjectId = useProjectStore((s) => s.currentProjectId);
  const activeDatasetId = useProjectStore((s) => s.activeDatasetId);

  const [projectId, setProjectId] = useState<string | null>(projectIdFromUrl ?? storeProjectId);
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState<TabKey>('ready');
  // Pending delete (Phase 1.6 — замена window.confirm на <ConfirmDialog>).
  const [pendingDelete, setPendingDelete] = useState<Dataset | null>(null);

  // Phase 2.2: ошибка скачивания (по dataset_id).
  const [downloadError, setDownloadError] = useState<Record<string, string>>({});
  const download = useDownloadDataset();

  // Phase 3: Preview modal — открывается из pending tab (failed/parsing).
  const [previewDatasetId, setPreviewDatasetId] = useState<string | null>(null);

  // Phase 1.7: bootstrap-guard.
  const { isFetched: projectsFetched } = useProjects();
  const bootstrapReady = projectsFetched && !!storeProjectId;

  const { data, isLoading } = useProjectDatasets(bootstrapReady ? projectId : null);
  const reparse = useReparseDataset();
  const remove = useDeleteDataset();
  const setActiveDataset = useSetActiveDataset();
  const setStoreActiveDataset = useProjectStore((s) => s.setActiveDataset);

  // Phase 2.1: подтянуть project.active_dataset_id с бэка → положить в store.
  useActiveDataset(bootstrapReady ? projectId : null);

  // === Синхронизация URL ↔ store ===
  useEffect(() => {
    if (storeProjectId && storeProjectId !== projectId) {
      setProjectId(storeProjectId);
      if (projectIdFromUrl !== storeProjectId) {
        setSearchParams({ project: storeProjectId }, { replace: true });
      }
      return;
    }
    if (!storeProjectId && projectIdFromUrl) {
      setProjectId(projectIdFromUrl);
    }
  }, [projectIdFromUrl, storeProjectId, projectId, setSearchParams]);

  const items = data?.items ?? [];
  const ready = useMemo(() => items.filter((d) => d.status === 'ready'), [items]);
  const pending = useMemo(
    () => items.filter((d) => PENDING_STATUSES.has(d.status)),
    [items],
  );

  const activeList = tab === 'ready' ? ready : pending;
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return activeList;
    return activeList.filter(
      (d) =>
        d.original_filename.toLowerCase().includes(f) ||
        d.name.toLowerCase().includes(f),
    );
  }, [activeList, filter]);

  // === Per-dataset handlers (closures over component scope) ===
  const handleOpen = (d: Dataset) => navigate(`/files/transform-results?id=${d.id}`);
  const handleTransform = (d: Dataset) => navigate(`/files/transform?id=${d.id}`);
  const handleProcessGeneration = (d: Dataset) =>
    navigate(`/process/generation?dataset=${d.id}`);
  const handlePreview = (d: Dataset) => setPreviewDatasetId(d.id);
  const handleDelete = (d: Dataset) => setPendingDelete(d);
  const handleReparse = async (d: Dataset) => {
    await reparse.mutateAsync(d.id);
  };
  const handleRefreshStatus = (d: Dataset) => {
    // Phase 2.7: invalidate только query для этого dataset'а.
    qc.invalidateQueries({ queryKey: ['datasets', d.id] });
    qc.invalidateQueries({ queryKey: ['datasets', 'project', projectId] });
  };
  const handleDownload = async (d: Dataset) => {
    try {
      setDownloadError((prev) => {
        if (!prev[d.id]) return prev;
        const { [d.id]: _drop, ...rest } = prev;
        return rest;
      });
      await download.mutateAsync(d.id);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('files.results.download_error', { defaultValue: 'Не удалось скачать файл' });
      setDownloadError((prev) => ({ ...prev, [d.id]: msg }));
    }
  };
  const handleToggleActive = async (d: Dataset) => {
    if (!projectId) return;
    const nextId = d.id === activeDatasetId ? null : d.id;
    setStoreActiveDataset(nextId);
    try {
      await setActiveDataset.mutateAsync({ projectId, datasetId: nextId });
    } catch {
      setStoreActiveDataset(null);
    }
  };

  // === Column defs === (inline — idempotent pinning effect в DataTable
  // защищает от render-loop при новом ref columns каждый рендер).
  const columns: ColumnDef<Dataset>[] = [
    {
      id: 'file',
      accessorKey: 'name',
      header: () => t('files.results.col_file', { defaultValue: 'Файл' }),
      cell: (info) => {
        const d = info.row.original;
        return (
          <div className="flex items-center gap-2 min-w-0">
            <FileTypeIcon fileType={d.file_type} className="w-4 h-4" />
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => handleOpen(d)}
                className="text-sm font-medium text-surface-100 hover:text-primary-300 truncate text-left block w-full"
                title={d.name}
              >
                {d.name}
              </button>
              <div className="text-[10px] text-surface-500 uppercase truncate">
                {d.file_type} · {formatBytes(d.file_size_bytes)}
              </div>
            </div>
          </div>
        );
      },
      size: 320,
    },
    {
      id: 'stage',
      accessorKey: 'status',
      header: () => t('files.results.col_stage', { defaultValue: 'Этап' }),
      cell: (info) => {
        const d = info.row.original;
        const isBusy = d.status === 'uploaded' || d.status === 'parsing';
        const isFailed = d.status === 'failed';
        const isActive = d.id === activeDatasetId;
        return (
          <div className="flex flex-col items-start gap-1">
            <span
              className={cn(
                'text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider inline-flex items-center gap-1 whitespace-nowrap',
                STATUS_TONE[d.status],
              )}
            >
              {isBusy && <Loader2 className="w-3 h-3 animate-spin" />}
              {t(STATUS_LABEL_KEY[d.status])}
            </span>
            {isActive && (
              <span
                className="text-[10px] px-2 py-0.5 rounded bg-success-tint/15 text-success border border-success-tint/30 uppercase tracking-wider inline-flex items-center gap-1 whitespace-nowrap"
                title={t('files.status.active_hint', {
                  defaultValue: 'Активный датасет проекта',
                })}
              >
                <CheckCircle2 className="w-3 h-3" />
                {t('files.status.active', { defaultValue: 'В работе' })}
              </span>
            )}
            {isFailed && d.error_message && (
              <span
                className="text-[10px] text-danger max-w-[16rem] truncate"
                title={d.error_message}
                data-testid="failed-error"
              >
                {t('files.results.failed_error', {
                  defaultValue: 'Ошибка обработки:',
                })}{' '}
                {d.error_message}
              </span>
            )}
          </div>
        );
      },
      size: 170,
      enableColumnFilter: false,
    },
    {
      id: 'rows',
      accessorFn: (d) => (d.status === 'ready' ? d.total_rows : null),
      header: () => t('files.results.col_rows', { defaultValue: 'Строк' }),
      cell: (info) => {
        const d = info.row.original;
        return (
          <div className="text-right tabular-nums text-surface-200">
            {d.status === 'ready' ? d.total_rows.toLocaleString() : '—'}
          </div>
        );
      },
      size: 80,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      id: 'cols',
      accessorFn: (d) => (d.status === 'ready' ? d.total_columns : null),
      header: () => t('files.results.col_cols', { defaultValue: 'Колонок' }),
      cell: (info) => {
        const d = info.row.original;
        return (
          <div className="text-right tabular-nums text-surface-200">
            {d.status === 'ready' ? d.total_columns : '—'}
          </div>
        );
      },
      size: 90,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      id: 'errors',
      accessorFn: (d) =>
        (d.raw_metadata as { error_count?: number } | null)?.error_count ?? 0,
      header: () => t('files.results.col_errors', { defaultValue: 'Ошибок' }),
      cell: (info) => {
        const d = info.row.original;
        if (d.status !== 'ready') return <span className="text-surface-500">—</span>;
        const errors =
          (d.raw_metadata as { error_count?: number } | null)?.error_count ?? 0;
        return (
          <span className={cn('tabular-nums', errors > 0 ? 'text-warning' : 'text-surface-500')}>
            {errors}
          </span>
        );
      },
      size: 90,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      id: 'source',
      accessorKey: 'original_filename',
      header: () => t('files.results.col_source', { defaultValue: 'Исходный файл' }),
      cell: (info) => {
        const d = info.row.original;
        const selectedSheet =
          (d.raw_metadata as { selected_sheet?: string } | null)?.selected_sheet;
        return (
          <div className="text-xs text-surface-400 truncate" title={d.original_filename}>
            {d.original_filename}
            {selectedSheet && (
              <div className="text-[10px] text-surface-500 mt-0.5 truncate">
                {t('files.transform.sheet_label', { defaultValue: 'Лист:' })}{' '}
                <span className="text-surface-300">{selectedSheet}</span>
              </div>
            )}
          </div>
        );
      },
      size: 200,
      enableColumnFilter: false,
    },
    {
      id: 'actions',
      header: () => t('files.results.col_actions', { defaultValue: 'Действия' }),
      cell: (info) => {
        const d = info.row.original;
        const isReady = d.status === 'ready';
        const isFailed = d.status === 'failed';
        return (
          <div className="flex items-center gap-1 justify-end flex-wrap">
            <RowAction
              onClick={() => handleToggleActive(d)}
              title={
                d.id === activeDatasetId
                  ? t('files.status.deactivate', { defaultValue: 'Снять активность' })
                  : t('files.status.activate', { defaultValue: 'Сделать активным' })
              }
              tone={d.id === activeDatasetId ? 'active' : 'default'}
              icon={d.id === activeDatasetId ? CheckCircle2 : Circle}
              label={t('files.status.active', { defaultValue: 'В работе' })}
              disabled={setActiveDataset.isPending}
            />
            <RowAction
              onClick={() => handleOpen(d)}
              title={t('files.results.act_open', { defaultValue: 'Открыть' })}
              tone="default"
              icon={Eye}
              label={t('files.results.act_open', { defaultValue: 'Открыть' })}
            />
            {tab === 'ready' && isReady ? (
              <RowAction
                onClick={() => handleProcessGeneration(d)}
                title={t('files.results.act_process', { defaultValue: 'Обработать' })}
                tone="primary"
                icon={Play}
                label={t('files.results.act_process', { defaultValue: 'Обработать' })}
                highlight
              />
            ) : (
              <>
                {!isReady && (
                  <RowAction
                    onClick={() => handlePreview(d)}
                    title={t('files.results.act_preview', { defaultValue: 'Предпросмотр' })}
                    tone="default"
                    icon={Eye}
                    label={t('files.results.act_preview', { defaultValue: 'Предпросмотр' })}
                  />
                )}
                {!isReady && (
                  <RowAction
                    onClick={() => handleTransform(d)}
                    title={t('files.results.act_transform', { defaultValue: 'Трансформировать' })}
                    tone="primary"
                    icon={Pencil}
                    label={t('files.results.act_transform', { defaultValue: 'Трансформировать' })}
                  />
                )}
              </>
            )}
            {isFailed && tab !== 'ready' && (
              <RowAction
                onClick={() => handleReparse(d)}
                title={t('files.results.act_retry', { defaultValue: 'Повторить' })}
                tone="warn"
                icon={Loader2}
                label={t('files.results.act_retry', { defaultValue: 'Повторить' })}
              />
            )}
            <DownloadButton
              dataset={d}
              onDownload={() => handleDownload(d)}
              isDownloading={download.isPending}
              variant="compact"
              error={downloadError[d.id]}
              testId={`dataset-download-button-${d.id}`}
            />
            <RowAction
              onClick={() => handleDelete(d)}
              title={t('files.results.act_delete', { defaultValue: 'Удалить' })}
              tone="danger"
              icon={Trash2}
              label="×"
            />
          </div>
        );
      },
      size: 380,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
  ];

  if (!projectId) {
    return (
      <div className="card text-sm text-surface-400 text-center py-8">
        {t('files.upload.no_project')}
      </div>
    );
  }

  if (!bootstrapReady) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
        <p className="text-sm text-surface-400">
          {t('files.upload.bootstrap_loading', { defaultValue: 'Загружаем проект…' })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 min-w-0 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <FileTypeIcon fileType="xlsx" className="w-5 h-5" />
        <h1 className="text-xl font-semibold">{t('files.results.title')}</h1>
        <div className="flex-1" />
        <div className="relative">
          <SearchIcon className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-surface-500 pointer-events-none" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('files.results.search_placeholder', { defaultValue: 'Поиск…' })}
            className="bg-surface-800 border border-surface-700 rounded pl-7 pr-2 py-1 text-xs w-48"
          />
        </div>
        <button type="button" onClick={() => navigate('/files/upload')} className="btn-primary">
          <Upload className="w-4 h-4" />
          <span>{t('files.results.upload_button', { defaultValue: 'Загрузить' })}</span>
        </button>
      </div>

      {/* Tabs */}
      <div role="tablist" className="flex items-stretch gap-0 border-b border-surface-800">
        <TabButton
          active={tab === 'ready'}
          onClick={() => setTab('ready')}
          count={ready.length}
          label={t('files.results.tab_ready', { defaultValue: 'Готово к работе' })}
          subLabel={t('files.results.tab_ready_sub', {
            defaultValue: 'Файлы, которые можно запустить в поиск и генерацию.',
          })}
        />
        <TabButton
          active={tab === 'pending'}
          onClick={() => setTab('pending')}
          count={pending.length}
          label={t('files.results.tab_pending', { defaultValue: 'Загружено — нужна трансформация' })}
          subLabel={t('files.results.tab_pending_sub', {
            defaultValue: 'Файлы, которые вы загрузили, но ещё не подготовили.',
          })}
        />
      </div>

      {/* Table — P1.3: единый <DataTable>. Loading/empty ветки сохраняют
          прежние тексты; aux-banner-строки (download-error / parsing-timeout)
          рендерятся через renderAuxRow, testids сохранены. */}
      {isLoading ? (
        <div className="card p-6 text-sm text-surface-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('common.loading')}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-6 text-sm text-surface-400 text-center">
          {items.length === 0
            ? t('files.results.empty_project', {
                defaultValue: 'В этом проекте пока нет загруженных файлов',
              })
            : tab === 'ready'
              ? t('files.results.empty_filter', { defaultValue: 'Нет готовых к работе файлов' })
              : t('files.results.empty_filter', {
                  defaultValue: 'Нет файлов, ожидающих трансформации',
                })}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          pinLeftCount={1}
          rowClassName={(d) =>
            cn('hover:bg-surface-800', d.id === activeDatasetId && 'bg-success-tint/5')
          }
          renderAuxRow={(d) => (
            <DatasetAuxRows
              d={d}
              downloadError={downloadError[d.id]}
              onReparse={() => handleReparse(d)}
              onRefreshStatus={() => handleRefreshStatus(d)}
            />
          )}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('files.list.delete_confirm_title', { defaultValue: 'Удалить файл' })}
        message={
          pendingDelete?.id === activeDatasetId
            ? t('files.list.delete_confirm_active', {
                name: pendingDelete.name || t('files.list.delete_fallback', {
                  defaultValue: 'этот файл',
                }),
              })
            : t('files.list.delete_confirm', {
                name: pendingDelete?.name || t('files.list.delete_fallback', {
                  defaultValue: 'этот файл',
                }),
              })
        }
        confirmLabel={t('common.delete', { defaultValue: 'Удалить' })}
        cancelLabel={t('common.cancel', { defaultValue: 'Отмена' })}
        confirmVariant="danger"
        busy={remove.isPending}
        onConfirm={async () => {
          if (pendingDelete) {
            const id = pendingDelete.id;
            const wasActive = id === activeDatasetId;
            try {
              await remove.mutateAsync(id);
              if (wasActive) {
                setStoreActiveDataset(null);
              }
            } finally {
              setPendingDelete(null);
            }
          }
        }}
        onClose={() => {
          if (!remove.isPending) setPendingDelete(null);
        }}
      />

      {/* Phase 3: Preview modal для pending tab (failed/parsing preview). */}
      <DataPreviewModal
        open={previewDatasetId !== null}
        datasetId={previewDatasetId}
        onClose={() => setPreviewDatasetId(null)}
      />
    </div>
  );
}

// === Aux banner rows for a dataset (rendered via DataTable.renderAuxRow).
//     Отдельный компонент — чтобы хук useParsingTimeout вызывался легально
//     (правила хуков: один вызов на компонент, не в цикле/рендер-пропе). ===

function DatasetAuxRows({
  d,
  downloadError,
  onReparse,
  onRefreshStatus,
}: {
  d: Dataset;
  downloadError?: string;
  onReparse: () => void;
  onRefreshStatus: () => void;
}) {
  const { t } = useTranslation();
  const isBusy = d.status === 'uploaded' || d.status === 'parsing';
  const isTimeout = useParsingTimeout(d);

  return (
    <>
      {downloadError && (
        <tr
          className="border-b border-surface-800 bg-danger-tint/5"
          data-testid="download-error-row"
        >
          <td colSpan={COL_COUNT} className="px-3 py-1.5 text-[11px] text-danger">
            <span className="font-medium">
              {t('files.results.download_error_short', {
                defaultValue: 'Ошибка скачивания:',
              })}
            </span>{' '}
            {downloadError}
          </td>
        </tr>
      )}
      {isBusy && isTimeout && (
        <tr
          className="border-b border-surface-800 bg-warning-tint/5"
          data-testid="dataset-row-timeout"
        >
          <td colSpan={COL_COUNT} className="px-3 py-2 text-xs text-warning">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-semibold">
                  {t('files.results.timeout.title', {
                    defaultValue: 'Обработка дольше обычного',
                  })}
                </p>
                <p className="text-warning/80 mt-0.5">
                  {t('files.results.timeout.hint', {
                    defaultValue:
                      'Возможно, задача ожидает обработки. Можно повторить, обновить статус или закрыть.',
                  })}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={onReparse}
                  className="text-xs px-2 py-1 rounded border border-warning-tint/40 text-warning hover:bg-warning-tint/10 inline-flex items-center gap-1"
                  aria-label={t('files.list.retry', { defaultValue: 'Повторить' })}
                  data-testid="dataset-row-timeout-retry"
                >
                  <RotateCw className="w-3 h-3" />
                  <span>{t('files.list.retry', { defaultValue: 'Повторить' })}</span>
                </button>
                <button
                  type="button"
                  onClick={onRefreshStatus}
                  className="text-xs px-2 py-1 rounded border border-surface-700 text-surface-200 hover:bg-surface-800"
                  aria-label={t('files.results.timeout.refresh', {
                    defaultValue: 'Обновить статус',
                  })}
                  data-testid="dataset-row-timeout-refresh"
                >
                  {t('files.results.timeout.refresh', { defaultValue: 'Обновить статус' })}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function TabButton({
  active,
  onClick,
  count,
  label,
  subLabel,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  label: string;
  subLabel: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex-1 min-w-[12rem] text-left px-4 py-3 border-b-2 -mb-px transition-colors',
        active
          ? 'border-primary-500 text-primary-200'
          : 'border-transparent text-surface-300 hover:text-surface-100',
      )}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <span>{label}</span>
        <span
          className={cn(
            'text-[10px] px-1.5 py-0.5 rounded tabular-nums',
            active ? 'bg-primary-500/20 text-primary-200' : 'bg-surface-800 text-surface-400',
          )}
        >
          {count}
        </span>
      </div>
      <div className="text-xs text-surface-500 mt-0.5">{subLabel}</div>
    </button>
  );
}

function RowAction({
  onClick,
  title,
  icon: Icon,
  label,
  tone,
  highlight = false,
  disabled = false,
}: {
  onClick: () => void;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  tone: 'default' | 'primary' | 'warn' | 'danger' | 'active';
  highlight?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      className={cn(
        'text-xs px-2 py-1 rounded border flex items-center gap-1 transition-colors',
        disabled && 'opacity-50 cursor-not-allowed',
        tone === 'default' && 'border-surface-700 text-surface-300 hover:border-surface-500',
        tone === 'primary' && 'border-primary-500/40 text-primary-200 hover:bg-primary-500/10',
        tone === 'warn' && 'border-warning-tint/40 text-warning hover:bg-warning-tint/10',
        tone === 'danger' && 'border-danger-tint/30 text-danger hover:bg-danger-tint/10',
        tone === 'active' &&
          'border-success-tint/40 bg-success-tint/10 text-success hover:bg-success-tint/15',
        highlight && 'shadow-sm shadow-primary-500/20 font-semibold',
      )}
    >
      <Icon className="w-3 h-3" />
      <span className="hidden lg:inline truncate max-w-[8rem]">{label}</span>
    </button>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}